import util from 'preact-util';
import { pickCurrentDisciplineClasses } from './conventionResults';
import { pickRubicClubOrgId } from './rubicClub';

// Map NSF/Rubic's gender value (format undocumented in our code) to the convention
// participant code 'k'/'m'. Defensive + case-insensitive; unknown/empty → '' so the
// caller leaves gender unchanged. Extend the lists if NSF returns an unexpected value.
export function rubicGenderToCode(raw) {
    const s = String(raw == null ? '' : raw).toLowerCase().trim();
    if (!s) return '';
    if (['m', 'male', 'man', 'mann', 'mannlig', 'gutt', '1'].includes(s)) return 'm';
    if (['k', 'f', 'female', 'woman', 'kvinne', 'kvinnelig', 'jente', '2'].includes(s)) return 'k';
    return '';
}

// Refresh a shooter's NSF/Rubic data: find the Rubic person, update the USER record
// (classifications, club, nsfId), and return what the caller needs to also re-snapshot
// the convention participant (club, skytterklasse, gender). Does NOT write gender to the
// user (not a user schema field) — that's the caller's job on the participant.
//   alwaysResolveClub: true  → re-resolve club even if the user already has nsfClubId
//                      false → resolve only when missing (preserves view-stage-admin)
//   branchId: Rubic branch (gren) GUID the convention is shot in. The club is the one the
//             shooter REPRESENTS in that branch (rubicPerson.branchOrganizations), not the
//             first active membership — see pickRubicClubOrgId.
export async function syncShooterFromRubic({ participant, conventionYear, appState, userStore, alwaysResolveClub = false, branchId = '' }) {
    if (!participant) return { ok: false, notFound: false };

    let response;
    if (participant.nsfId) {
        response = await appState.rubicGetPersonById(participant.nsfId);
    } else {
        const query = { name: `${(participant.firstname || '').trim()} ${(participant.lastname || '').trim()}`.trim() };
        if (participant.birth) query.birthDate = util.isoDate(participant.birth, false, false, true);
        if (participant.cellphone) query.cellphone = participant.cellphone;
        if (participant.email) query.email = participant.email;
        response = await appState.rubicGetPersonSearch(query);
    }

    const { data } = response || {};
    let rubicPerson = null;
    if (Array.isArray(data) && data.length === 1) rubicPerson = data[0];
    else if (data && data.id) rubicPerson = data;

    if (!rubicPerson || !rubicPerson.id) {
        return { ok: false, notFound: true, participant };
    }

    const { id, firstName, lastName, rubicPersonId, gender, memberships, disciplineClassifications, branchOrganizations } = rubicPerson;
    const currentClasses = disciplineClassifications
        ? pickCurrentDisciplineClasses(disciplineClassifications, conventionYear)
        : [];
    const membershipOrgIds = Array.isArray(memberships)
        ? memberships.map(m => m.organizationId).filter(Boolean)
        : [];
    const updateObject = {
        nsfId: rubicPersonId,
        rubicId: id,
        'nsfDiciplineClasses-overwrite': currentClasses,
        nsfBranchOrganizations: branchOrganizations,
        'nsfMembershipOrgIds-overwrite': membershipOrgIds,
    };

    let club = null;
    const shouldResolveClub = alwaysResolveClub || !participant.nsfClubId;
    if (shouldResolveClub) {
        const clubOrgId = pickRubicClubOrgId({ memberships, branchOrganizations, branchId });
        if (clubOrgId) {
            const clubResponse = await appState.rubicGetClubById({ rubicOrganizationId: clubOrgId });
            if (clubResponse && clubResponse.status === 200 && clubResponse.data) {
                const { niF_OrganizationId, organizationName } = clubResponse.data;
                club = {
                    sportsClub: organizationName || '',
                    nsfClubNumber: niF_OrganizationId || '',
                    nsfClubId: clubOrgId,
                };
                updateObject.sportsClub = club.sportsClub;
                updateObject.nsfClubNumber = club.nsfClubNumber;
                updateObject.nsfClubId = club.nsfClubId;
            }
        }
    }

    await userStore.adminUpdate(participant.id, updateObject);

    return {
        ok: true,
        notFound: false,
        participant,
        rubicPersonId,
        name: `${firstName || ''} ${lastName || ''}`.trim(),
        currentClasses,
        club,
        genderCode: rubicGenderToCode(gender),
        rawGender: gender || '',
    };
}
